import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Card } from '../../components/card';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { Symbol } from '../../components/symbol';
import { SkeletonRows } from '../../components/skeleton';
import { StatusBadge } from '../../components/status-badge';
import { Toggle } from '../../components/toggle';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import {
  CrudDrawer,
  Field,
  SelectField,
  TextField,
  TextAreaField,
  CheckField,
  extractErr,
} from '../../components/crud-drawer';

// DLT SMS templates. Wired to /admin/sms-templates.
//
// Operators only deliver SMS whose text matches a template approved on STPL (the
// DLT portal), so approval can't happen here. This page tracks each template
// through that process and holds the ids MSG91 sends by:
//   write/copy text → submit on STPL → paste DLT id → create on MSG91 → paste
//   MSG91 id → mark Approved → switch on.

type Status = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';
type Audience = 'CUSTOMER' | 'SELLER' | 'DELIVERY_PARTNER' | 'SELLER_OR_PARTNER' | 'ANY';

interface SmsTemplate {
  id: string;
  key: string;
  name: string;
  category: string;
  content: string;
  variables: string[];
  dltTemplateId: string | null;
  msg91TemplateId: string | null;
  status: Status;
  statusNote: string | null;
  isEnabled: boolean;
  updatedAt: string;
  audience: Audience | null;
  trigger: string | null;
  availableVars: string[];
  alwaysOn: boolean;
}

const STATUS_LABEL: Record<Status, string> = {
  DRAFT: 'Draft',
  // "In review" maps to the warning tone in StatusBadge.
  SUBMITTED: 'In review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft — not submitted yet' },
  { value: 'SUBMITTED', label: 'Submitted on STPL — waiting' },
  { value: 'APPROVED', label: 'Approved on STPL' },
  { value: 'REJECTED', label: 'Rejected on STPL' },
];

const CATEGORY_OPTIONS = [
  { value: 'TRANSACTIONAL', label: 'Transactional (OTP)' },
  { value: 'SERVICE_IMPLICIT', label: 'Service implicit' },
  { value: 'SERVICE_EXPLICIT', label: 'Service explicit' },
  { value: 'PROMOTIONAL', label: 'Promotional' },
];

const AUDIENCE_ORDER: { key: Audience; label: string }[] = [
  { key: 'ANY', label: 'Login' },
  { key: 'CUSTOMER', label: 'Customers' },
  { key: 'SELLER', label: 'Sellers' },
  { key: 'DELIVERY_PARTNER', label: 'Delivery partners' },
  { key: 'SELLER_OR_PARTNER', label: 'Sellers and delivery partners' },
];

/** Realistic values for previews and the test send. */
const SAMPLE: Record<string, string> = {
  OTP: '482913',
  otp: '4829',
  order_no: 'UTSMF3K2Q9A7X',
  amount: '1499',
  item: 'Banarasi Silk Saree',
  item_count: '2',
  courier: 'Delhivery',
  tracking_no: 'DL1234567890',
  reason: 'Photo is not clear',
  document: 'PAN card',
  utr: 'UTR1234567890',
};

type Filter = 'all' | 'action' | 'live';

const DLT_VAR_MAX = 30;
const PLACEHOLDER = '{#var#}';
// GSM-7 basic set. Anything outside it (₹, emoji, smart quotes) makes the whole
// SMS Unicode: 70 characters a part instead of 160.
const GSM7 = /^[A-Za-z0-9 \r\n@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[~\]|€]*$/;

const countPlaceholders = (s: string) => s.split(PLACEHOLDER).length - 1;
const parseVars = (s: string) => s.split(',').map((v) => v.trim()).filter(Boolean);

function render(content: string, vars: string[], values: Record<string, string>): string {
  let i = 0;
  return content.replace(/\{#var#\}/g, () => (values[vars[i++] ?? ''] ?? '').slice(0, DLT_VAR_MAX));
}

function msg91Text(content: string, vars: string[]): string {
  let i = 0;
  return content.replace(/\{#var#\}/g, () => `##${vars[i++] ?? '?'}##`);
}

function smsParts(text: string): { chars: number; parts: number; unicode: boolean } {
  const unicode = !GSM7.test(text);
  const chars = text.length;
  const [single, multi] = unicode ? [70, 67] : [160, 153];
  return { chars, parts: chars <= single ? 1 : Math.ceil(chars / multi), unicode };
}

async function copy(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error('Could not copy — select the text and copy it manually');
  }
}

interface Form {
  name: string;
  category: string;
  content: string;
  variables: string;
  dltTemplateId: string;
  msg91TemplateId: string;
  status: Status;
  statusNote: string;
  isEnabled: boolean;
}

function toForm(t: SmsTemplate): Form {
  return {
    name: t.name,
    category: t.category,
    content: t.content,
    variables: t.variables.join(', '),
    dltTemplateId: t.dltTemplateId ?? '',
    msg91TemplateId: t.msg91TemplateId ?? '',
    status: t.status,
    statusNote: t.statusNote ?? '',
    isEnabled: t.isEnabled,
  };
}

const isLive = (t: SmsTemplate) =>
  t.status === 'APPROVED' && !!t.msg91TemplateId && (t.isEnabled || t.alwaysOn);

export function SmsTemplatesPage() {
  const [rows, setRows] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');

  const [editing, setEditing] = useState<SmsTemplate | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);

  const [testPhone, setTestPhone] = useState('');
  const [testValues, setTestValues] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const { confirm } = useConfirm();

  const load = async () => {
    try {
      const { data } = await api.get<SmsTemplate[]>('/admin/sms-templates');
      setRows(data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const counts = useMemo(
    () => ({
      all: rows.length,
      live: rows.filter(isLive).length,
      action: rows.filter((r) => r.status !== 'APPROVED').length,
    }),
    [rows],
  );

  // Client-side: every template is loaded. Matches what each row shows — name,
  // trigger, DLT text, status (and STPL note), both IDs and the On column —
  // plus the code key, category and the audience heading it sits under.
  const { query, setQuery, filtered: searched } = useTableSearch(rows, (t) => [
    t.name,
    t.key,
    t.trigger,
    t.content,
    STATUS_LABEL[t.status],
    t.statusNote,
    t.dltTemplateId ?? 'no DLT ID',
    t.msg91TemplateId ?? 'no MSG91 ID',
    CATEGORY_OPTIONS.find((c) => c.value === t.category)?.label ?? t.category,
    AUDIENCE_ORDER.find((a) => a.key === (t.audience ?? 'ANY'))?.label,
    t.alwaysOn ? 'Always on' : t.isEnabled ? 'On' : 'Off',
    isLive(t) ? 'Live' : '',
  ]);

  const grouped = useMemo(() => {
    const visible = searched.filter((r) =>
      filter === 'live' ? isLive(r) : filter === 'action' ? r.status !== 'APPROVED' : true,
    );
    return AUDIENCE_ORDER.map((a) => ({ ...a, items: visible.filter((r) => (r.audience ?? 'ANY') === a.key) })).filter(
      (g) => g.items.length > 0,
    );
  }, [searched, filter]);
  const matchCount = grouped.reduce((n, g) => n + g.items.length, 0);

  // ---- Drawer ---------------------------------------------------------------

  const openEdit = (t: SmsTemplate) => {
    setEditing(t);
    setForm(toForm(t));
    setTestValues(Object.fromEntries(t.availableVars.map((v) => [v, SAMPLE[v] ?? ''])));
  };

  const close = () => {
    setEditing(null);
    setForm(null);
  };

  const formVars = form ? parseVars(form.variables) : [];
  const slots = form ? countPlaceholders(form.content) : 0;
  const reworded = !!editing && !!form && form.content.trim() !== editing.content && editing.status !== 'DRAFT';
  const unknownVars = editing ? formVars.filter((v) => !editing.availableVars.includes(v)) : [];
  const preview = form ? render(form.content, formVars, { ...SAMPLE, ...testValues }) : '';
  const parts = smsParts(preview);

  const save = async () => {
    if (!editing || !form) return;
    if (slots !== formVars.length) {
      toast.error(`The text has ${slots} {#var#} but ${formVars.length} variables are listed`);
      return;
    }
    if (reworded) {
      const ok = await confirm({
        title: 'Save as a new draft?',
        description:
          'The wording changed, so this is a new DLT template. Saving moves it back to Draft, clears both template IDs and switches it off until STPL approves the new text.',
        confirmLabel: 'Save as draft',
        tone: 'primary',
      });
      if (!ok) return;
    } else if (!editing.alwaysOn && editing.isEnabled && !form.isEnabled) {
      // Unticking "Send this SMS" stops a live message — ask, like the row switch.
      const ok = await confirm({
        title: `Switch off ${editing.name}?`,
        description: 'This SMS stops being sent as soon as you save. You can switch it back on at any time.',
        confirmLabel: 'Save and switch off',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const { data } = await api.put<SmsTemplate & { resetToDraft: boolean }>(`/admin/sms-templates/${editing.id}`, {
        name: form.name.trim(),
        category: form.category,
        content: form.content.trim(),
        variables: formVars,
        dltTemplateId: form.dltTemplateId.trim() || null,
        msg91TemplateId: form.msg91TemplateId.trim() || null,
        status: form.status,
        statusNote: form.statusNote.trim() || null,
        ...(editing.alwaysOn ? {} : { isEnabled: form.isEnabled }),
      });
      toast.success(data.resetToDraft ? 'Saved as a new draft — submit it on STPL' : 'Template saved');
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  /** Row switch — the common edit once a template is approved. */
  const toggleEnabled = async (t: SmsTemplate, next: boolean) => {
    if (!next) {
      const ok = await confirm({
        title: `Switch off ${t.name}?`,
        description: 'This SMS stops being sent straight away. You can switch it back on at any time.',
        confirmLabel: 'Switch off',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setRows((cur) => cur.map((r) => (r.id === t.id ? { ...r, isEnabled: next } : r)));
    try {
      await api.put(`/admin/sms-templates/${t.id}`, { isEnabled: next });
      toast.success(next ? `${t.name} is live` : `${t.name} switched off`);
    } catch (err) {
      toast.error(extractErr(err));
      setRows((cur) => cur.map((r) => (r.id === t.id ? { ...r, isEnabled: !next } : r)));
    }
  };

  const sendTest = async () => {
    if (!editing) return;
    if (!testPhone.trim()) {
      toast.error('Enter the mobile number to send the test to');
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post<{ sent: boolean; reason: string | null; detail: string | null; to: string }>(
        `/admin/sms-templates/${editing.id}/test`,
        { phone: testPhone.trim(), values: testValues },
      );
      if (data.sent) {
        toast.success(`Test SMS sent to ${data.to}`, { description: 'Uses the saved template — save first if you changed it.' });
      } else {
        toast.error(`Not sent — ${data.reason ?? 'unknown'}`, {
          description: data.detail ?? 'Check the SMS gateway on Platform config (enabled, MSG91, API key).',
        });
      }
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setTesting(false);
    }
  };

  // ---- Render ---------------------------------------------------------------

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="SMS templates" />
        <Card padding="none"><SkeletonRows cols={4} /></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="SMS templates"
        description="Every SMS must match a template approved on STPL, word for word. Track each template here and add its IDs, then switch it on."
      />

      <Card>
        <ol className="grid gap-4 text-[13.5px] text-ink-700 md:grid-cols-4">
          {[
            ['content_copy', 'Copy the DLT text', 'Submit it on STPL with header SANMAG. Set status to Submitted.'],
            ['verified', 'Paste the DLT ID', 'Once STPL approves, add its template ID here.'],
            ['sms', 'Create it on MSG91', 'Use the MSG91 text (##name## variables) and the DLT ID. Paste the MSG91 template ID here.'],
            ['toggle_on', 'Approve and switch on', 'Set status to Approved, send a test, then turn it on.'],
          ].map(([icon, title, text], i) => (
            <li key={title} className="flex gap-3">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-admin-soft text-admin-ink">
                <Symbol name={icon!} size={18} />
              </span>
              <span className="min-w-0">
                <span className="block font-semibold text-ink-900">{i + 1}. {title}</span>
                <span className="text-ink-500">{text}</span>
              </span>
            </li>
          ))}
        </ol>
      </Card>

      <div className="flex flex-wrap gap-2">
        {([
          ['all', `All (${counts.all})`],
          ['action', `Not approved yet (${counts.action})`],
          ['live', `Live (${counts.live})`],
        ] as [Filter, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            aria-pressed={filter === key}
            className="fchip"
          >
            {label}
          </button>
        ))}
      </div>

      <TableSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by name, text, status, DLT or MSG91 ID, or audience"
        resultCount={query.trim() ? matchCount : undefined}
      />

      {grouped.length === 0 && query.trim() ? (
        <EmptyState
          icon="search_off"
          title={`No matches for “${query.trim()}”`}
          body="Try another word, pick another filter, or clear the search to see every template."
          action={<button type="button" onClick={() => setQuery('')} className="btn btn-secondary">Clear search</button>}
        />
      ) : grouped.length === 0 && (
        <EmptyState
          icon="sms"
          title="No templates match this filter"
          body="Pick another filter above to see the rest of the templates."
        />
      )}

      {grouped.map((g) => (
        <Card key={g.key} padding="none">
          <div className="border-b border-surface-line px-5 py-3">
            <h2 className="text-[16px] font-semibold text-ink-900">{g.label}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table min-w-[40rem]">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Status</th>
                  <th className="hidden lg:table-cell">DLT / MSG91 ID</th>
                  <th className="text-right">On</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {g.items.map((t) => (
                  <tr key={t.id} className="align-top">
                    <td>
                      <div className="font-semibold text-ink-900">{t.name}</div>
                      {t.trigger && <div className="mt-0.5 text-[12.5px] text-ink-500">{t.trigger}</div>}
                      <div className="mt-1 line-clamp-2 max-w-xl text-[12.5px] text-ink-400">{t.content}</div>
                    </td>
                    <td>
                      <StatusBadge status={STATUS_LABEL[t.status]} />
                      {t.status === 'REJECTED' && t.statusNote && (
                        <div className="field-error mt-1 max-w-[12rem]">{t.statusNote}</div>
                      )}
                    </td>
                    <td className="sku hidden text-ink-700 lg:table-cell">
                      <div>{t.dltTemplateId ?? <span className="text-ink-300">no DLT ID</span>}</div>
                      <div>{t.msg91TemplateId ?? <span className="text-ink-300">no MSG91 ID</span>}</div>
                    </td>
                    <td>
                      <div className="flex justify-end">
                        {t.alwaysOn ? (
                          <span
                            title="Login OTPs are always sent once approved. Switching them off would lock everyone out."
                            className="badge badge-info"
                          >
                            Always on
                          </span>
                        ) : (
                          <Toggle
                            checked={t.isEnabled}
                            // Only an approved template with both ids can go live; the API
                            // enforces it too, this just avoids offering a dead switch.
                            disabled={t.status !== 'APPROVED' || !t.dltTemplateId || !t.msg91TemplateId}
                            aria-label={`${t.isEnabled ? 'Switch off' : 'Switch on'} ${t.name}`}
                            onChange={(v) => void toggleEnabled(t, v)}
                          />
                        )}
                      </div>
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        onClick={() => openEdit(t)}
                        className="btn btn-ghost btn-sm"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      <CrudDrawer
        open={editing !== null && form !== null}
        title={editing?.name ?? ''}
        subtitle={editing?.trigger ?? undefined}
        busy={busy}
        onClose={close}
        onSubmit={() => void save()}
      >
        {editing && form && (
          <>
            <TextField
              label="Name on STPL"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              maxLength={80}
              mono
              hint={`Used by the code as ${editing.key}.`}
            />
            <SelectField
              label="Category"
              value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              options={CATEGORY_OPTIONS}
            />

            <TextAreaField
              label="DLT template text"
              value={form.content}
              onChange={(v) => setForm({ ...form, content: v })}
              rows={5}
              hint="Exactly as registered on STPL. Use {#var#} for each value (30 characters max each)."
            />
            <div className="-mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void copy(form.content.trim(), 'DLT text')}
                className="btn btn-secondary btn-sm"
              >
                <Symbol name="content_copy" size={14} /> Copy for STPL
              </button>
              <span className={slots === formVars.length ? 'text-[12.5px] text-ink-500' : 'field-error font-semibold'}>
                {slots} {'{#var#}'} · {formVars.length} variable{formVars.length === 1 ? '' : 's'}
              </span>
            </div>
            {reworded && (
              <div className="alert alert-warning text-[13.5px]">
                New wording needs a new STPL approval. Saving moves this back to Draft and clears both IDs.
              </div>
            )}

            <TextField
              label="Variables, in order"
              value={form.variables}
              onChange={(v) => setForm({ ...form, variables: v })}
              mono
              placeholder={editing.availableVars.join(', ') || 'none'}
              hint={`One per {#var#}, comma separated. Available: ${editing.availableVars.join(', ') || 'none'}.`}
            />
            {unknownVars.length > 0 && (
              <p className="field-error -mt-2 font-semibold">
                Not available for this template: {unknownVars.join(', ')}
              </p>
            )}

            <Field label="MSG91 template text" hint="Paste into MSG91 → SMS → Templates with the DLT template ID.">
              <div className="rounded-sm bg-surface-sunk px-3 py-2 font-mono text-[12.5px] text-ink-700">
                {msg91Text(form.content, formVars)}
              </div>
            </Field>
            <div className="-mt-2">
              <button
                type="button"
                onClick={() => void copy(msg91Text(form.content.trim(), formVars), 'MSG91 text')}
                className="btn btn-secondary btn-sm"
              >
                <Symbol name="content_copy" size={14} /> Copy for MSG91
              </button>
            </div>

            <Field label="What the recipient reads">
              <div className="rounded-sm border border-surface-line px-3 py-2 text-[13.5px] text-ink-900">{preview}</div>
            </Field>
            <p className={`-mt-2 text-[12.5px] ${parts.unicode || parts.parts > 1 ? 'font-semibold text-warning-ink' : 'text-ink-500'}`}>
              {parts.chars} characters · {parts.parts} SMS credit{parts.parts === 1 ? '' : 's'}
              {parts.unicode && ' · Unicode — remove ₹, emoji or smart quotes (use Rs.)'}
            </p>

            <SelectField
              label="Status"
              value={form.status}
              onChange={(v) => setForm({ ...form, status: v as Status })}
              options={STATUS_OPTIONS}
            />
            <TextAreaField
              label="Note"
              value={form.statusNote}
              onChange={(v) => setForm({ ...form, statusNote: v })}
              rows={2}
              placeholder="STPL's rejection reason, submission date…"
            />
            <TextField
              label="DLT template ID"
              value={form.dltTemplateId}
              onChange={(v) => setForm({ ...form, dltTemplateId: v.replace(/\s/g, '') })}
              mono
              maxLength={40}
              placeholder="1107…"
              hint="From STPL after approval."
            />
            <TextField
              label="MSG91 template ID"
              value={form.msg91TemplateId}
              onChange={(v) => setForm({ ...form, msg91TemplateId: v.replace(/\s/g, '') })}
              mono
              maxLength={60}
              hint={editing.alwaysOn ? 'The ID of this template in MSG91 → SMS → Templates (not the OTP section). Replaces MSG91_OTP_TEMPLATE_ID.' : 'From MSG91 after you create the template there.'}
            />
            {editing.alwaysOn ? (
              <div className="alert alert-info text-[13.5px]">
                Login OTPs are always sent. This ID is used once the status is Approved.
              </div>
            ) : (
              <CheckField
                label="Send this SMS"
                hint="Needs status Approved and both IDs. Also needs the SMS gateway and SMS notifications switched on in Platform config."
                checked={form.isEnabled}
                disabled={form.status !== 'APPROVED' || !form.dltTemplateId || !form.msg91TemplateId}
                onChange={(v) => setForm({ ...form, isEnabled: v })}
              />
            )}

            <div className="card space-y-3 p-4">
              <div>
                <h3 className="text-[14.5px] font-semibold text-ink-900">Send a test</h3>
                <p className="text-[12.5px] text-ink-500">
                  Sends the saved template through MSG91, even if it is switched off.
                </p>
              </div>
              <TextField label="Mobile number" value={testPhone} onChange={setTestPhone} placeholder="98765 43210" mono />
              {editing.availableVars.map((v) => (
                <TextField
                  key={v}
                  label={v}
                  value={testValues[v] ?? ''}
                  onChange={(val) => setTestValues((cur) => ({ ...cur, [v]: val }))}
                  mono
                />
              ))}
              <button
                type="button"
                disabled={testing}
                onClick={() => void sendTest()}
                className="btn btn-tonal btn-sm"
              >
                <Symbol name="send" size={16} /> {testing ? 'Sending…' : 'Send test SMS'}
              </button>
            </div>
          </>
        )}
      </CrudDrawer>
    </div>
  );
}
